import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeApi, projectApi, userApi, companyApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, Save, Upload, FileText, Trash2, Camera, User, Key, ChevronDown } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SalaryTab } from './SalaryTab';
import { DEPARTMENTS, DESIGNATIONS, withCurrentOption } from '@/lib/employeeOptions';
const genders = ['Male', 'Female', 'Other'];
const maritalStatuses = ['Single', 'Married', 'Divorced', 'Widowed'];
const employmentTypes = ['Full-time', 'Part-time', 'Contract', 'Intern'];
const grades = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6'];
const DOC_TYPES = ['Aadhaar Card', 'PAN Card', 'Resume', 'Offer Letter', 'Employment Contract', 'Experience Letter', 'Education Certificate', 'Other'];

/** Calendar order Mon–Sun; matches locale weekday names used in attendance. */
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const LEAVE_BALANCE_FIELDS = [
  { key: 'EL', label: 'Earned Leave (EL)', shortLabel: 'Earned Leave', color: 'text-[#0047AB]' },
  { key: 'CL', label: 'Casual Leave (CL)', shortLabel: 'Casual Leave', color: 'text-[#6366f1]' },
  { key: 'ML', label: 'Medical Leave (ML)', shortLabel: 'Medical Leave', color: 'text-[#10b981]' },
  { key: 'LOP', label: 'LOP Taken', shortLabel: 'LOP Taken', color: 'text-[#ef4444]' },
];

function normalizeLeaveBalance(value) {
  const src = value && typeof value === 'object' ? value : {};
  return Object.fromEntries(
    LEAVE_BALANCE_FIELDS.map(({ key }) => [key, Number(src[key]) || 0])
  );
}

function normalizeWeeklyOffDays(value) {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',').map(s => s.trim()).filter(Boolean)
      : [];
  const lowerToCanonical = Object.fromEntries(WEEKDAYS.map((d) => [d.toLowerCase(), d]));
  const seen = new Set();
  for (const item of raw) {
    const canon = lowerToCanonical[String(item ?? '').trim().toLowerCase()];
    if (canon) seen.add(canon);
  }
  return WEEKDAYS.filter((d) => seen.has(d));
}

/** `dob` is `YYYY-MM-DD` from `<input type="date" />`. Returns full years or null. */
function getAgeFromIsoDate(dob) {
  if (!dob?.trim()) return null;
  const parts = dob.split('-');
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const mo = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (!y || !mo || !d) return null;
  const birth = new Date(y, mo - 1, d);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  if (age < 0 || age > 150) return null;
  return age;
}

/** Indian mobile: 10 digits, first digit 6–9. Accepts `9876543210`, `+91…`, `091…`. */
function extractIndiaMobile10Digits(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  let d = s.replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('91')) d = d.slice(-10);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  else if (d.length > 10) d = d.slice(-10);
  if (d.length !== 10 || !/^[6-9]\d{9}$/.test(d)) return null;
  return d;
}

function normalizePhoneToE164India(raw) {
  const ten = extractIndiaMobile10Digits(raw);
  if (!ten) return null;
  return `+91${ten}`;
}

/** Up to 10 digits for the input; strips pasted `+91…` / leading 0. Allows partial entry while typing. */
function clampIndiaMobileInputDigits(raw) {
  let d = String(raw ?? '').replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  else if (d.length > 10) d = d.slice(-10);
  return d.slice(0, 10);
}

function normalizeAadhaarDigits(raw) {
  if (raw == null || String(raw).trim() === '') return '';
  return String(raw).replace(/\D/g, '').slice(0, 12);
}

function buildEmailFromFirstName(firstName) {
  const cleaned = String(firstName ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  if (!cleaned) return '';
  return `${cleaned}@abd.com`;
}

/** `null` = valid (including empty). Otherwise an error message for `toast.error`. */
function getAadhaarValidationError(raw) {
  const d = normalizeAadhaarDigits(raw);
  if (!d) return null;
  if (d.length !== 12) return 'Aadhaar must be exactly 12 digits';
  if (!/^[2-9]\d{11}$/.test(d)) return 'Invalid Aadhaar: cannot start with 0 or 1';
  return null;
}

/** `null` = cap disabled. `0` and other numbers stay as entered. */
function normalizeSalaryLimit(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return null;
  return n;
}

function normalizeSalaryAsOnMarch(value) {
  if (value == null || value === '') return 0;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n);
}

/** Payload for create/update: `age` from DOB, phones as +91…, Aadhaar digits only. */
function buildEmployeePayload(form) {
  const computedAge = getAgeFromIsoDate(form.dob);
  const phone = normalizePhoneToE164India(form.phone) ?? '';
  const ecPhoneRaw = form.emergencyContact?.phone?.trim?.() ? form.emergencyContact.phone : '';
  const emergencyPhone = ecPhoneRaw ? normalizePhoneToE164India(form.emergencyContact.phone) ?? '' : '';
  const aadhaarNumber = normalizeAadhaarDigits(form.aadhaarNumber);
  const monthlyLeaveAllowance = form?.monthlyLeaveAllowanceAll ? 0 : (form?.monthlyLeaveAllowance ?? 0);
  const extra = form?.salary?.extraReimbursement || {};
  const {
    effectivePfLimit: _effectivePfLimit,
    effectiveEsicThreshold: _effectiveEsicThreshold,
    project: _salaryProject,
    grossSalary: _grossSalary,
    inHandSalary: _inHandSalary,
    ctc: _ctc,
    ...salaryFields
  } = form?.salary || {};
  const pfLimit = normalizeSalaryLimit(form?.salary?.employerPfLimit);
  const esicLimit = normalizeSalaryLimit(form?.salary?.esicThreshold);
  const capsOn = pfLimit != null || esicLimit != null;
  return {
    ...form,
    age: computedAge ?? null,
    phone,
    aadhaarNumber,
    monthlyLeaveAllowance,
    leaveBalance: normalizeLeaveBalance(form?.leaveBalance),
    emergencyContact: {
      ...form.emergencyContact,
      phone: emergencyPhone,
    },
    salary: {
      ...salaryFields,
      extraReimbursement: {
        extraReimbursementFrequency:
          extra.extraReimbursementFrequency === 'Yearly' ? 'Yearly' : 'Monthly',
        extraReimbursementAmount: Number(extra.extraReimbursementAmount) || 0,
      },
      employerPfLimit: capsOn ? (pfLimit ?? 0) : null,
      esicThreshold: capsOn ? (esicLimit ?? 0) : null,
      applyPfCeilingToEmployee: Boolean(form?.salary?.applyPfCeilingToEmployee),
      salaryAsOnMarch: normalizeSalaryAsOnMarch(form?.salary?.salaryAsOnMarch),
    },
  };
}

const emptyEmployee = {
  firstName: '', lastName: '', fatherName: '', picture: '', gender: '', dob: '', age: null, maritalStatus: '', phone: '', email: '',
  department: '', designation: '', company: '', grade: '', reportingManager: '', employmentType: '',
  shiftStart: '09:00', shiftEnd: '18:00',
  joiningDate: '', confirmationDate: '', isConfirmed: false, workLocation: '', projects: [], status: 'Active', role: 'Employee',
  monthlyLeaveAllowance: 2, monthlyLeaveAllowanceAll: false, weeklyOffDays: ['Sunday'], showHoliday: false,
  leaveBalance: { EL: 0, CL: 0, ML: 0, LOP: 0 },
  pfNumber: '', esiNumber: '', uanNumber: '', panNumber: '', aadhaarNumber: '', bankName: '', bankAccount: '', ifscCode: '', address: '',
  emergencyContact: { name: '', relation: '', phone: '' },
  salary: {
    basic: 0,
    hra: 0,
    specialAllowance: 0,
    conveyance: 0,
    telephoneExpense: 0,
    mediclaim: 0,
    tds: 0,
    epf: 0,
    esic: 0,
    bonus: 0,
    salaryAsOnMarch: 0,
    employerPF: 0,
    employerESIC: 0,
    gratuity: 0,
    employerPfLimit: null,
    esicThreshold: null,
    applyPfCeilingToEmployee: false,
    effectivePfLimit: null,
    effectiveEsicThreshold: null,
    project: null,
    extraReimbursement: {
      extraReimbursementFrequency: 'Monthly',
      extraReimbursementAmount: 0,
      inHandSalaryAfterExtraReimbursement: 0,
      inHandSalaryAfterExtraReimbursementPerAnnum: 0,
      totalCtcAfterExtraReimbursement: 0,
      totalCtcAfterExtraReimbursementPerAnnum: 0,
    },
  },
  documents: [],
};

function IndianMobilePrefixInput({ id, value, onValueChange, dataTestId, placeholder, required }) {
  return (
    <div className="flex h-9 w-full min-w-0 overflow-hidden rounded-md border border-input bg-background text-sm shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring">
      <span
        className="flex shrink-0 items-center border-r border-input bg-muted px-2.5 text-muted-foreground select-none sm:px-3"
        aria-hidden="true"
      >
        +91
      </span>
      <Input
        id={id}
        data-testid={dataTestId}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        maxLength={10}
        required={required}
        aria-required={required ? true : undefined}
        placeholder={placeholder ?? 'Mobile number'}
        className="h-9 min-w-0 flex-1 rounded-none border-0 bg-transparent px-3 py-1 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 md:text-sm"
        value={value}
        onChange={(e) => onValueChange(clampIndiaMobileInputDigits(e.target.value))}
      />
    </div>
  );
}

function FormField({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest font-medium text-muted-foreground">{label}</Label>{children}</div>;
}

const employeeDisplayName = (employee) =>
  `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim();

const sortEmployeesByName = (list) =>
  [...(Array.isArray(list) ? list : [])].sort((a, b) =>
    employeeDisplayName(a).localeCompare(employeeDisplayName(b), undefined, { sensitivity: 'base' })
  );

export default function EmployeeFormPage() {
  const employeeFormTabs = ['personal', 'job', 'documents', 'uploads', 'salary'];
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = id && id !== 'new';
  const fileInputRef = useRef(null);
  const pictureInputRef = useRef(null);
  const [form, setForm] = useState({ ...emptyEmployee });
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('Aadhaar Card');
  const [loading, setLoading] = useState(!!isEdit);
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState('');
  const [hasCredentials, setHasCredentials] = useState(false);
  const [weeklyOffOpen, setWeeklyOffOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [docToDelete, setDocToDelete] = useState(null);
  const prevFirstNameRef = useRef('');

  useEffect(() => {
    employeeApi.getAll({ sortBy: 'name' }).then((data) => {
      setEmployees(sortEmployeesByName(data));
    }).catch(() => {});
    projectApi.getAll().then(setProjects).catch(() => {});
    companyApi.getAll().then(setCompanies).catch(() => {});
    if (isEdit) {
      employeeApi.getById(id).then(emp => {
        const merged = { ...emptyEmployee, ...emp, documents: emp.documents || [], projects: emp.projects || [] };
        merged.weeklyOffDays = normalizeWeeklyOffDays(emp?.weeklyOffDays);
        merged.leaveBalance = normalizeLeaveBalance(emp?.leaveBalance);
        merged.aadhaarNumber = normalizeAadhaarDigits(emp?.aadhaarNumber ?? '');
        merged.phone = clampIndiaMobileInputDigits(emp?.phone ?? '');
        merged.emergencyContact = {
          ...merged.emergencyContact,
          phone: clampIndiaMobileInputDigits(emp?.emergencyContact?.phone ?? ''),
        };
        merged.showHoliday = emp?.showHoliday === true;
        merged.salary = {
          ...emptyEmployee.salary,
          ...(emp?.salary || {}),
          extraReimbursement: {
            ...emptyEmployee.salary.extraReimbursement,
            ...(emp?.salary?.extraReimbursement || {}),
          },
          employerPfLimit: normalizeSalaryLimit(emp?.salary?.employerPfLimit),
          esicThreshold: normalizeSalaryLimit(emp?.salary?.esicThreshold),
          salaryAsOnMarch: normalizeSalaryAsOnMarch(emp?.salary?.salaryAsOnMarch),
          applyPfCeilingToEmployee: emp?.salary?.applyPfCeilingToEmployee === true,
          effectivePfLimit: emp?.salary?.effectivePfLimit ?? null,
          effectiveEsicThreshold: emp?.salary?.effectiveEsicThreshold ?? null,
          project: emp?.salary?.project || null,
        };
        setForm(merged);
        setLoading(false);
        if (emp.empId) {
          userApi.check(emp.empId).then(res => setHasCredentials(!!res.hasCredentials)).catch(() => {});
        }
      }).catch(() => { toast.error('Employee not found'); navigate('/employees'); });
    }
  }, [id, isEdit, navigate]);

  useEffect(() => {
    if (!companies?.length) return;
    if (!form?.company) return;
    const hasCompanyId = companies.some((c) => c?.companyId === form.company);
    if (hasCompanyId) return;
    const matchedByName = companies.find((c) => c?.name === form.company);
    if (!matchedByName?.companyId) return;
    setForm((prev) => ({ ...prev, company: matchedByName.companyId }));
  }, [companies, form?.company]);

  useEffect(() => {
    if (isEdit) return;
    const previousFirstName = prevFirstNameRef.current;
    const previousAutoEmail = buildEmailFromFirstName(previousFirstName);
    const nextAutoEmail = buildEmailFromFirstName(form?.firstName);
    const currentEmail = String(form?.email ?? '').trim();
    const canAutoUpdate = !currentEmail || currentEmail === previousAutoEmail;

    if (canAutoUpdate && currentEmail !== nextAutoEmail) {
      setForm((prev) => ({ ...prev, email: nextAutoEmail }));
    }

    prevFirstNameRef.current = form?.firstName ?? '';
  }, [form?.firstName, form?.email, isEdit]);

  const handleChange = (field, value) => {
    setForm(prev => {
      const keys = field.split('.');
      if (keys.length === 2) return { ...prev, [keys[0]]: { ...prev[keys[0]], [keys[1]]: value } };
      return { ...prev, [field]: value };
    });
  };

  const handleSalaryChange = (field, value) => {
    setForm((prev) => {
      const isLimitField = field === 'employerPfLimit' || field === 'esicThreshold';
      let nextValue;
      if (field === 'applyPfCeilingToEmployee') {
        nextValue = Boolean(value);
      } else if (isLimitField) {
        if (value == null) {
          nextValue = null;
        } else {
          const parsed = parseInt(value, 10);
          nextValue = Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
        }
      } else {
        nextValue = parseInt(value, 10) || 0;
      }
      return { ...prev, salary: { ...prev.salary, [field]: nextValue } };
    });
  };

  const handleSalaryIncremented = (salary) => {
    if (!salary || typeof salary !== 'object') return;
    setForm((prev) => ({
      ...prev,
      salary: {
        ...prev.salary,
        ...salary,
        extraReimbursement: {
          ...(prev.salary?.extraReimbursement || {}),
          ...(salary.extraReimbursement || {}),
        },
        employerPfLimit: normalizeSalaryLimit(
          Object.prototype.hasOwnProperty.call(salary, 'employerPfLimit')
            ? salary.employerPfLimit
            : prev.salary?.employerPfLimit
        ),
        esicThreshold: normalizeSalaryLimit(
          Object.prototype.hasOwnProperty.call(salary, 'esicThreshold')
            ? salary.esicThreshold
            : prev.salary?.esicThreshold
        ),
        salaryAsOnMarch: normalizeSalaryAsOnMarch(
          salary.salaryAsOnMarch ?? prev.salary?.salaryAsOnMarch
        ),
        applyPfCeilingToEmployee:
          (salary.applyPfCeilingToEmployee ?? prev.salary?.applyPfCeilingToEmployee) === true,
        effectivePfLimit: salary.effectivePfLimit ?? prev.salary?.effectivePfLimit ?? null,
        effectiveEsicThreshold: salary.effectiveEsicThreshold ?? prev.salary?.effectiveEsicThreshold ?? null,
        project: salary.project ?? prev.salary?.project ?? null,
      },
    }));
  };

  const handleExtraReimbursementChange = (extraReimbursement) => {
    setForm((prev) => ({
      ...prev,
      salary: {
        ...prev.salary,
        extraReimbursement: {
          ...(prev.salary?.extraReimbursement || {}),
          ...extraReimbursement,
        },
      },
    }));
  };

  const handleProjectToggle = (projectId) => {
    setForm(prev => {
      const currentProjects = prev.projects || [];
      if (currentProjects.includes(projectId)) {
        return { ...prev, projects: currentProjects.filter(p => p !== projectId) };
      } else {
        return { ...prev, projects: [...currentProjects, projectId] };
      }
    });
  };

  const handleWeeklyOffToggle = (day) => {
    setForm((prev) => {
      const current = [...(prev.weeklyOffDays || [])];
      const idx = current.indexOf(day);
      if (idx >= 0) {
        return { ...prev, weeklyOffDays: current.filter((d) => d !== day) };
      }
      const next = [...current, day].sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));
      return { ...prev, weeklyOffDays: next };
    });
  };

  const handlePictureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Picture size must be under 2MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setForm(prev => ({ ...prev, picture: evt.target.result }));
      toast.success('Profile picture updated');
    };
    reader.readAsDataURL(file);
    if (pictureInputRef.current) pictureInputRef.current.value = '';
  };

  const handleDocUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File size must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setForm(prev => ({ ...prev, documents: [...(prev.documents || []), {
        docId: `DOC${Date.now()}`, type: selectedDocType, fileName: file.name,
        fileSize: (file.size / 1024).toFixed(1) + ' KB', mimeType: file.type,
        data: evt.target.result, uploadedAt: new Date().toISOString(), uploadedBy: user.name,
      }] }));
      toast.success(`${selectedDocType} uploaded`);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeDoc = (docId) => {
    setForm(prev => ({ ...prev, documents: (prev.documents || []).filter(d => d.docId !== docId) }));
  };

  const handleConfirmRemoveDoc = () => {
    if (!docToDelete?.docId) return;
    removeDoc(docToDelete.docId);
    setDocToDelete(null);
  };

  const handleSubmit = async () => {
    if (form.aadhaarNumber?.trim()) {
      const aadhaarErr = getAadhaarValidationError(form.aadhaarNumber);
      if (aadhaarErr) {
        toast.error(aadhaarErr);
        return;
      }
    }
    if (form.phone?.trim()) {
      if (!normalizePhoneToE164India(form.phone)) {
        toast.error('Enter a valid 10-digit Indian mobile number.');
        return;
      }
    }
    if (form.emergencyContact?.phone?.trim()) {
      if (!normalizePhoneToE164India(form.emergencyContact.phone)) {
        toast.error('Emergency contact: enter a valid 10-digit Indian mobile number.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = buildEmployeePayload(form);
      if (isEdit) {
        await employeeApi.update(id, payload);
        if (password) {
          if (hasCredentials) {
            await userApi.reset(form.empId, { password });
            toast.success('Employee updated & password reset successfully');
          } else {
            // No existing account — create one
            if (!form.email) {
              toast.warning('Employee updated, but no email set — login account not created');
            } else {
              try {
                await userApi.create({
                  email: form.email,
                  password,
                  employeeId: form.empId,
                  name: `${form.firstName} ${form.lastName}`.trim(),
                  role: form.role || 'Employee',
                });
                setHasCredentials(true);
                toast.success('Employee updated & login account created successfully');
              } catch (credErr) {
                toast.warning(`Employee updated, but login account failed: ${credErr.message}`);
              }
            }
          }
        } else {
          toast.success('Employee updated successfully');
        }
      } else {
        const result = await employeeApi.create(payload);
        if (password) {
          if (!form.email) {
            toast.warning(`Employee added (ID: ${result.empId}), but no email set — login account not created`);
          } else {
            try {
              await userApi.create({
                email: form.email,
                password,
                employeeId: result.empId,
                name: `${form.firstName} ${form.lastName}`.trim(),
                role: form.role || 'Employee',
              });
              toast.success(`Employee added & login account created! ID: ${result.empId}`);
            } catch (credErr) {
              toast.warning(`Employee added (ID: ${result.empId}), but login account failed: ${credErr.message}`);
            }
          }
        } else {
          toast.success(`Employee added! ID: ${result.empId}`);
        }
      }
      navigate('/employees');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const currentTabIndex = employeeFormTabs.indexOf(activeTab);
  const hasNextTab = currentTabIndex >= 0 && currentTabIndex < employeeFormTabs.length - 1;
  const effectiveMonthlyLeaveAllowance = form?.monthlyLeaveAllowanceAll ? 0 : (form?.monthlyLeaveAllowance ?? 2);

  const handleNextTab = () => {
    if (!hasNextTab) return;
    const nextTab = employeeFormTabs[currentTabIndex + 1];
    if (!nextTab) return;
    setActiveTab(nextTab);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="employee-form-page" className="space-y-3 animate-fadeIn">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/employees')} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div><h1 className="text-2xl font-bold tracking-tight font-[Manrope]">{isEdit ? 'Edit Employee' : 'Add New Employee'}</h1>{isEdit && <p className="text-sm text-muted-foreground font-mono">{id}</p>}</div>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="bg-muted p-1">
          <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
          <TabsTrigger value="job" data-testid="tab-job">Job</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Statutory & Bank</TabsTrigger>
          <TabsTrigger value="uploads" data-testid="tab-uploads">Documents</TabsTrigger>
          <TabsTrigger value="salary" data-testid="tab-salary">Salary</TabsTrigger>
        </TabsList>
        <TabsContent value="personal">
          <Card className="border border-border shadow-sm"><CardHeader><CardTitle className="text-base font-[Manrope]">Personal Information</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                <div className="w-full rounded-lg border border-border bg-muted/30 p-4 lg:w-72 lg:shrink-0">
                  <div className="flex items-start gap-4 lg:flex-col lg:items-center lg:text-center">
                    <div className="relative">
                      {form.picture ? (
                        <img src={form.picture} alt="Profile" className="w-24 h-24 rounded-full object-cover border-2 border-border" />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-[#0047AB]/10 flex items-center justify-center">
                          <User className="w-10 h-10 text-[#0047AB]/50" />
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1">
                        <input ref={pictureInputRef} type="file" accept="image/*" onChange={handlePictureUpload} className="hidden" data-testid="picture-input" />
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-full" onClick={() => pictureInputRef.current?.click()} data-testid="upload-picture-btn">
                          <Camera className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="min-w-0 text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">Profile Picture</p>
                      <p>Upload a professional photo. Max 2MB, JPG/PNG format.</p>
                      {form.picture && <Button variant="link" className="p-0 h-auto text-red-500 text-xs" onClick={() => handleChange('picture', '')}>Remove picture</Button>}
                    </div>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <FormField label="First Name"><Input data-testid="input-firstName" value={form.firstName} onChange={e => handleChange('firstName', e.target.value)} /></FormField>
                    <FormField label="Last Name"><Input data-testid="input-lastName" value={form.lastName} onChange={e => handleChange('lastName', e.target.value)} /></FormField>
                    <FormField label="Father's Name"><Input data-testid="input-fatherName" value={form.fatherName} onChange={e => handleChange('fatherName', e.target.value)} placeholder="Father's full name" /></FormField>
                    <FormField label="Gender"><Select value={form.gender} onValueChange={v => handleChange('gender', v)}><SelectTrigger data-testid="select-gender"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{genders.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></FormField>
                    <div className="flex w-full min-w-0 flex-row flex-wrap items-start gap-x-3 gap-y-1.5">
                      <div className="min-w-0 flex-1 basis-[10rem] space-y-1.5 sm:max-w-[11rem]">
                        <Label htmlFor="employee-dob" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">
                          Date of Birth
                        </Label>
                        <Input
                          id="employee-dob"
                          data-testid="input-dob"
                          type="date"
                          className="w-full"
                          value={form.dob}
                          onChange={e => handleChange('dob', e.target.value)}
                        />
                      </div>
                      <div className="w-[4.5rem] shrink-0 space-y-1.5">
                        <Label htmlFor="employee-age" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">
                          Age
                        </Label>
                        <Input
                          id="employee-age"
                          data-testid="input-age"
                          type="text"
                          readOnly
                          aria-readonly="true"
                          tabIndex={-1}
                          className="w-full bg-muted/50"
                          value={getAgeFromIsoDate(form.dob) ?? ''}
                          placeholder="—"
                        />
                      </div>
                    </div>
                    <FormField label="Marital Status"><Select value={form.maritalStatus} onValueChange={v => handleChange('maritalStatus', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{maritalStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></FormField>
                    <FormField label="Phone">
                      <IndianMobilePrefixInput
                        id="employee-phone"
                        dataTestId="input-phone"
                        placeholder="1234567890"
                        value={form.phone}
                        onValueChange={(v) => handleChange('phone', v)}
                      />
                    </FormField>
                    <FormField label="Email"><Input data-testid="input-email" type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} /></FormField>
                    <FormField label="Address"><Input data-testid="input-address" value={form.address} onChange={e => handleChange('address', e.target.value)} /></FormField>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm mt-4"><CardHeader><CardTitle className="text-base font-[Manrope]">Emergency Contact</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField label="Contact Name"><Input value={form.emergencyContact.name} onChange={e => handleChange('emergencyContact.name', e.target.value)} /></FormField>
              <FormField label="Relation"><Input value={form.emergencyContact.relation} onChange={e => handleChange('emergencyContact.relation', e.target.value)} /></FormField>
              <FormField label="Phone">
                <IndianMobilePrefixInput
                  id="emergency-phone"
                  placeholder="Optional"
                  value={form.emergencyContact.phone}
                  onValueChange={(v) => handleChange('emergencyContact.phone', v)}
                />
              </FormField>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="job">
          <Card className="border border-border shadow-sm"><CardHeader><CardTitle className="text-base font-[Manrope]">Job Information</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <FormField label="Company">
                <Select value={form.company} onValueChange={v => handleChange('company', v)}>
                  <SelectTrigger data-testid="select-company"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.companyId} value={company.companyId}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Department"><Select value={form.department} onValueChange={v => handleChange('department', v)}><SelectTrigger data-testid="select-department"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{withCurrentOption(DEPARTMENTS, form.department).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Designation"><Select value={form.designation} onValueChange={v => handleChange('designation', v)}><SelectTrigger data-testid="select-designation"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{withCurrentOption(DESIGNATIONS, form.designation).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Grade"><Select value={form.grade} onValueChange={v => handleChange('grade', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Reporting Manager"><Select value={form.reportingManager} onValueChange={v => handleChange('reportingManager', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{employees.filter(e => e.id !== id && e.empId !== id).map(e => <SelectItem key={e.id || e.empId} value={e.id || e.empId}>{employeeDisplayName(e)}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Employment Type"><Select value={form.employmentType} onValueChange={v => handleChange('employmentType', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{employmentTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Joining Date"><Input type="date" value={form.joiningDate} onChange={e => handleChange('joiningDate', e.target.value)} /></FormField>
              <FormField label="Work Location"><Input value={form.workLocation} onChange={e => handleChange('workLocation', e.target.value)} /></FormField>
              <FormField label="Status"><Select value={form.status} onValueChange={v => handleChange('status', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="Draft">Draft</SelectItem></SelectContent></Select></FormField>
              <FormField label="System Role">
                <Select value={form.role || 'Employee'} onValueChange={v => handleChange('role', v)}>
                  <SelectTrigger data-testid="select-role"><SelectValue placeholder="Select Role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="Employee">Employee</SelectItem>
                  </SelectContent>
                </Select>
               
              </FormField>
            </CardContent>
          </Card>
          {/* Confirmation Status
          <Card className="border border-border shadow-sm mt-4"><CardHeader><CardTitle className="text-base font-[Manrope]">Confirmation Status</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <Checkbox id="isConfirmed" checked={form.isConfirmed} onCheckedChange={v => handleChange('isConfirmed', v)} data-testid="checkbox-confirmed" />
                <label htmlFor="isConfirmed" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Employee is confirmed
                </label>
                {form.isConfirmed && <Badge className="bg-green-100 text-green-700 border-green-200">Confirmed</Badge>}
              </div>
              {form.isConfirmed && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Confirmation Date"><Input type="date" value={form.confirmationDate} onChange={e => handleChange('confirmationDate', e.target.value)} data-testid="input-confirmationDate" /></FormField>
                </div>
              )}
            </CardContent>
          </Card>
          */}
          {/* Leave & Attendance Settings */}
          <Card className="border border-border shadow-sm mt-4">
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <CardTitle className="text-base font-[Manrope]">Leave & Attendance Settings</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Configure monthly leave allowance and weekly off days for this employee</p>
                </div>
                <div className="rounded-lg bg-muted/50 border border-border p-3 h-fit lg:max-w-md xl:max-w-lg">
                  <p className="text-xs font-medium">Current Settings</p>
                  <p className="text-sm mt-1">
                    <span className="font-mono text-[#0047AB]">{effectiveMonthlyLeaveAllowance}</span> leave days/month allowed
                    {' | '}
                    Apply to all: <span className="font-mono">{form.monthlyLeaveAllowanceAll ? 'Yes' : 'No'}</span>
                    {' | '}
                    Weekly off: <span className="font-mono">{form.weeklyOffDays?.length ? form.weeklyOffDays.join(', ') : 'None'}</span>
                    {' | '}
                    Show holidays: <span className="font-mono">{form.showHoliday === true ? 'Yes' : 'No'}</span>
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div>
                  <FormField label="Monthly Leave Allowance (Days)">
                    <div className="flex flex-wrap items-center gap-3">
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="31"
                        value={effectiveMonthlyLeaveAllowance}
                        onChange={e => handleChange('monthlyLeaveAllowance', parseFloat(e.target.value) || 0)}
                        data-testid="input-monthlyLeaveAllowance"
                        placeholder="e.g., 2"
                        className="w-24 sm:w-28"
                        disabled={!!form.monthlyLeaveAllowanceAll}
                      />
                      <div className="flex items-center gap-2">
                        <Switch
                          id="monthly-leave-all"
                          checked={!!form.monthlyLeaveAllowanceAll}
                          onCheckedChange={(checked) => {
                            const isApplyAll = !!checked;
                            handleChange('monthlyLeaveAllowanceAll', isApplyAll);
                            if (isApplyAll) handleChange('monthlyLeaveAllowance', 0);
                          }}
                          data-testid="toggle-monthlyLeaveAllowanceAll"
                          aria-label="Apply monthly leave allowance to all employees"
                        />
                        <Label htmlFor="monthly-leave-all" className="text-xs normal-case tracking-normal text-foreground">
                          Apply all
                        </Label>
                      </div>
                    </div>
                  </FormField>
                  <p className="text-xs text-muted-foreground mt-1">Max allowed leave days per month (can be 0.5 for half day)</p>
                </div>
                <div className="min-w-0">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1 sm:max-w-xs">
                      <FormField label="Weekly Days Off">
                        <Popover open={weeklyOffOpen} onOpenChange={setWeeklyOffOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              aria-expanded={weeklyOffOpen}
                              data-testid="select-weeklyOff"
                              className={cn(
                                'h-9 w-full justify-between font-normal text-left shadow-sm',
                                !(form.weeklyOffDays?.length) && 'text-muted-foreground'
                              )}
                            >
                              <span className="truncate">
                                {form.weeklyOffDays?.length ? form.weeklyOffDays.join(', ') : 'None'}
                              </span>
                              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-[var(--radix-popover-trigger-width)] p-1"
                            align="start"
                            data-testid="weekly-off-dropdown-content"
                          >
                            <div
                              className="flex max-h-64 flex-col gap-0.5 overflow-y-auto p-0.5"
                              role="listbox"
                              aria-label="Weekly days off"
                              data-testid="weekly-off-days-group"
                            >
                              {WEEKDAYS.map((day) => {
                                const checked = (form.weeklyOffDays || []).includes(day);
                                const id = `weekly-off-${day}`;
                                return (
                                  <div
                                    key={day}
                                    className="flex items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground sm:py-1.5"
                                  >
                                    <Checkbox
                                      id={id}
                                      checked={checked}
                                      onCheckedChange={() => handleWeeklyOffToggle(day)}
                                      data-testid={`checkbox-weeklyOff-${day}`}
                                    />
                                    <label htmlFor={id} className="flex-1 cursor-pointer select-none leading-none">
                                      {day}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </FormField>
                    </div>
                    <div className="shrink-0">
                      <FormField label="Show Holidays">
                        <div className="flex h-9 items-center gap-2">
                          <Switch
                            id="show-holiday"
                            checked={form.showHoliday === true}
                            onCheckedChange={(checked) => handleChange('showHoliday', !!checked)}
                            data-testid="toggle-showHoliday"
                            aria-label="Show holidays for this employee"
                          />
                          <Label htmlFor="show-holiday" className="text-xs normal-case tracking-normal text-foreground whitespace-nowrap">
                            {form.showHoliday === true ? 'Yes' : 'No'}
                          </Label>
                        </div>
                      </FormField>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Select weekly off days and whether company holidays apply</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:col-span-2">
                  <div>
                    <FormField label="Shift Start">
                      <Input
                        type="time"
                        value={form.shiftStart ?? '09:00'}
                        onChange={e => handleChange('shiftStart', e.target.value)}
                        data-testid="input-shiftStart"
                      />
                    </FormField>
                    <p className="text-xs text-muted-foreground mt-1">Daily shift start time</p>
                  </div>
                  <div>
                    <FormField label="Shift End">
                      <Input
                        type="time"
                        value={form.shiftEnd ?? '18:00'}
                        onChange={e => handleChange('shiftEnd', e.target.value)}
                        data-testid="input-shiftEnd"
                      />
                    </FormField>
                    <p className="text-xs text-muted-foreground mt-1">Daily shift end time</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm font-medium mb-3">Leave Balance</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {LEAVE_BALANCE_FIELDS.map(({ key, label }) => (
                    <FormField key={key} label={label}>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={form?.leaveBalance?.[key] ?? 0}
                        onChange={(e) => handleChange(`leaveBalance.${key}`, parseFloat(e.target.value) || 0)}
                        data-testid={`input-leaveBalance-${key}`}
                        className="font-mono"
                      />
                    </FormField>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Set opening leave balances for this employee (EL, CL, ML, LOP)</p>
              </div>
            </CardContent>
          </Card>
          {/* Project Assignment */}
          <Card className="border border-border shadow-sm mt-4"><CardHeader><CardTitle className="text-base font-[Manrope]">Project Assignment</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">Assign employee to one or more projects</p>
              {projects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {projects.map(proj => (
                    <div key={proj.projectId} className={`p-3 rounded-lg border cursor-pointer transition-all ${(form.projects || []).includes(proj.projectId) ? 'border-[#0047AB] bg-[#0047AB]/5' : 'border-border hover:border-[#0047AB]/30'}`} onClick={() => handleProjectToggle(proj.projectId)} data-testid={`project-${proj.projectId}`}>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={(form.projects || []).includes(proj.projectId)} onChange={() => {}} />
                        <div>
                          <p className="text-sm font-medium">{proj.name}</p>
                          <p className="text-xs text-muted-foreground">{proj.code} - {proj.location}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No projects available. Create projects from the hierarchy tab.</p>
              )}
              {(form.projects || []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="text-xs text-muted-foreground">Assigned:</span>
                  {(form.projects || []).map(pid => {
                    const proj = projects.find(p => p.projectId === pid);
                    return proj ? <Badge key={pid} variant="outline" className="text-xs">{proj.name}</Badge> : null;
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          {/* Login Credentials */}
          <Card className="border border-border shadow-sm mt-4">
            <CardHeader>
              <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
                <Key className="w-4 h-4 text-[#0047AB]" />
                Login Credentials
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEdit ? (
                <>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${hasCredentials ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-muted text-muted-foreground border border-border'}`}>
                      {hasCredentials ? 'Login account exists' : 'No login account yet'}
                    </span>
                    {hasCredentials && <span className="text-xs text-muted-foreground">Email: <span className="font-mono">{form.email || '—'}</span></span>}
                  </div>
                  <FormField label={hasCredentials ? 'Reset Password' : 'Set Password'}>
                    <Input
                      data-testid="input-reset-password"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={hasCredentials ? 'Enter new password to reset (leave blank to keep current)' : 'Set a password to create login access'}
                      autoComplete="new-password"
                    />
                  </FormField>
                  <p className="text-xs text-muted-foreground">{hasCredentials ? 'Leave blank to keep the existing password unchanged.' : 'Leave blank to skip. Login email will be the employee email above.'}</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">Optionally set a login password for this employee. The login email will be their email address above.</p>
                  <FormField label="Password">
                    <Input
                      data-testid="input-create-password"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Set password (leave blank to skip)"
                      autoComplete="new-password"
                    />
                  </FormField>
                  <p className="text-xs text-muted-foreground">Leave blank to skip creating a login account now. You can set it later by editing the employee.</p>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="documents">
          <Card className="border border-border shadow-sm"><CardHeader><CardTitle className="text-base font-[Manrope]">Statutory & Bank Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField label="PAN"><Input value={form.panNumber} onChange={e => handleChange('panNumber', e.target.value)} /></FormField>
              <FormField label="Aadhaar">
                <Input
                  data-testid="input-aadhaar"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="12-digit number"
                  maxLength={12}
                  className="font-mono"
                  value={form.aadhaarNumber}
                  onChange={e => handleChange('aadhaarNumber', e.target.value.replace(/\D/g, '').slice(0, 12))}
                />
              </FormField>
              <FormField label="PF Number"><Input value={form.pfNumber} onChange={e => handleChange('pfNumber', e.target.value)} /></FormField>
              <FormField label="ESI Number"><Input value={form.esiNumber} onChange={e => handleChange('esiNumber', e.target.value)} /></FormField>
              <FormField label="UAN"><Input value={form.uanNumber} onChange={e => handleChange('uanNumber', e.target.value)} /></FormField>
              <FormField label="Bank Name"><Input value={form.bankName} onChange={e => handleChange('bankName', e.target.value)} /></FormField>
              <FormField label="Account No."><Input value={form.bankAccount} onChange={e => handleChange('bankAccount', e.target.value)} /></FormField>
              <FormField label="IFSC"><Input value={form.ifscCode} onChange={e => handleChange('ifscCode', e.target.value)} /></FormField>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="uploads">
          <Card className="border border-border shadow-sm"><CardHeader><CardTitle className="text-base font-[Manrope]">Upload Documents</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-3">
                <div className="flex-1"><Label className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Document Type</Label><Select value={selectedDocType} onValueChange={setSelectedDocType}><SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger><SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div className="relative"><input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleDocUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="doc-file-input" /><Button variant="outline" data-testid="upload-doc-btn"><Upload className="w-4 h-4 mr-2" /> Choose File</Button></div>
              </div>
              {(form.documents || []).length > 0 ? (
                <div className="space-y-2 mt-4">{form.documents.map(doc => (
                  <div key={doc.docId} className="flex items-center justify-between p-3 rounded-lg border border-border" data-testid={`uploaded-doc-${doc.docId}`}>
                    <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-[#0047AB]/10 flex items-center justify-center"><FileText className="w-5 h-5 text-[#0047AB]" /></div><div><p className="text-sm font-medium">{doc.type}</p><p className="text-xs text-muted-foreground">{doc.fileName} ({doc.fileSize})</p></div></div>
                    <div className="flex items-center gap-2"><Badge variant="outline" className="text-xs">{new Date(doc.uploadedAt).toLocaleDateString()}</Badge><Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setDocToDelete(doc)}><Trash2 className="w-3.5 h-3.5" /></Button></div>
                  </div>
                ))}</div>
              ) : (
                <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg mt-4"><Upload className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No documents uploaded yet</p><p className="text-xs mt-1">Upload documents if available</p></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="salary">
          <SalaryTab
            form={form}
            projects={projects}
            employeeId={isEdit ? id : undefined}
            handleSalaryChange={handleSalaryChange}
            handleExtraReimbursementChange={handleExtraReimbursementChange}
            onSalaryIncremented={handleSalaryIncremented}
          />
        </TabsContent>
      </Tabs>
      <div className="flex justify-end gap-3 pb-8">
        <Button variant="outline" onClick={() => navigate('/employees')} data-testid="cancel-btn">Cancel</Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleNextTab}
          disabled={!hasNextTab}
          data-testid="next-tab-btn"
        >
          Next
        </Button>
        <Button onClick={handleSubmit} disabled={saving} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="save-employee-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : isEdit ? 'Update Employee' : 'Add Employee'}</Button>
      </div>

      <ConfirmDialog
        open={!!docToDelete}
        onOpenChange={(open) => !open && setDocToDelete(null)}
        title="Remove Document?"
        description={`Remove "${docToDelete?.type || 'this document'}" (${docToDelete?.fileName || ''}) from this employee?`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={handleConfirmRemoveDoc}
        data-testid="employee-doc-delete-confirm"
      />
    </div>
  );
}
